import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type MediaAsset } from '../api.js';

/**
 * Editor de capa do Reels.
 *
 * A API do Instagram aceita a capa de duas formas, e `cover_url` tem
 * precedência sobre `thumb_offset`:
 *
 *  - `thumb_offset`: um milissegundo do vídeo — o Instagram usa aquele frame.
 *  - `cover_url`: uma imagem própria.
 *
 * Aqui o usuário faz as duas coisas de uma vez: escolhe o frame com o controle
 * deslizante e, se escrever um título, geramos uma imagem daquele frame com o
 * texto por cima e usamos como `cover_url`. Sem título, mandamos só o offset e
 * deixamos o Instagram cortar o frame — não faz sentido gerar imagem igual.
 */

export type CoverConfig = {
  /** Milissegundo do vídeo escolhido como capa. */
  thumbOffsetMs: number;
  /** Título escrito sobre a capa. Vazio = sem imagem gerada. */
  title: string;
  /** Mídia da capa já gerada e enviada, quando há título. */
  generated: MediaAsset | null;
};

export function emptyCover(): CoverConfig {
  return { thumbOffsetMs: 0, title: '', generated: null };
}

/** Quebra o título em linhas que caibam na largura, medindo no próprio canvas. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const palavras = text.split(/\s+/).filter(Boolean);
  const linhas: string[] = [];
  let atual = '';
  for (const palavra of palavras) {
    const tentativa = atual ? `${atual} ${palavra}` : palavra;
    if (ctx.measureText(tentativa).width <= maxWidth || !atual) {
      atual = tentativa;
    } else {
      linhas.push(atual);
      atual = palavra;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}

export function CoverEditor({
  media,
  cover,
  onChange,
  suggestedTitle,
}: {
  media: MediaAsset | null;
  cover: CoverConfig;
  onChange: (patch: Partial<CoverConfig>) => void;
  suggestedTitle?: string | null;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [duration, setDuration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const isVideo = media?.kind === 'VIDEO';

  // Desenha o frame atual + o título no canvas de prévia.
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 1080x1920 é a resolução de capa que o Instagram espera para 9:16.
    canvas.width = 1080;
    canvas.height = 1920;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (video && video.videoWidth > 0) {
      // "cover": preenche sem distorcer, cortando o excesso.
      const escala = Math.max(
        canvas.width / video.videoWidth,
        canvas.height / video.videoHeight,
      );
      const w = video.videoWidth * escala;
      const h = video.videoHeight * escala;
      ctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
    }

    const titulo = cover.title.trim();
    if (!titulo) return;

    ctx.font = 'bold 96px system-ui, sans-serif';
    ctx.textAlign = 'center';
    const linhas = wrapText(ctx, titulo.toUpperCase(), canvas.width - 140);
    const alturaLinha = 116;
    const blocoAltura = linhas.length * alturaLinha;
    const topo = canvas.height * 0.5 - blocoAltura / 2;

    // Faixa escura atrás do texto: sem isso, título claro sobre frame claro
    // some — e a capa é justamente o que decide o clique.
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, topo - 56, canvas.width, blocoAltura + 96);

    ctx.fillStyle = '#fff';
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    linhas.forEach((linha, i) => {
      const y = topo + i * alturaLinha + 72;
      ctx.strokeText(linha, canvas.width / 2, y);
      ctx.fillText(linha, canvas.width / 2, y);
    });
  }, [cover.title]);

  /**
   * Redesenho após QUALQUER seek concluído — listener persistente.
   *
   * A versão anterior criava um listener `{once:true}` a cada mudança do
   * slider e o destruía na limpeza do efeito. Arrastando rápido, o `seeked` do
   * seek anterior chegava DEPOIS da limpeza: o desenho final se perdia, a
   * prévia congelava num frame velho e a capa escolhida na publicação não era
   * a que estava na tela. Com o listener fixo, todo seek concluído redesenha —
   * o navegador colapsa seeks em rajada e o último sempre dispara o evento.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;
    const aoAtualizar = () => render();
    video.addEventListener('seeked', aoAtualizar);
    // loadeddata: o primeiro frame só é desenhável a partir daqui.
    video.addEventListener('loadeddata', aoAtualizar);
    return () => {
      video.removeEventListener('seeked', aoAtualizar);
      video.removeEventListener('loadeddata', aoAtualizar);
    };
  }, [isVideo, render]);

  // Reposiciona o vídeo quando o slider muda. Só posiciona: quem desenha é o
  // listener acima, quando o frame de fato estiver disponível.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;
    if (video.readyState < 1) return; // sem metadata ainda; onLoadedMetadata cuida
    video.currentTime = cover.thumbOffsetMs / 1000;
  }, [cover.thumbOffsetMs, isVideo]);

  useEffect(() => {
    render();
  }, [render]);

  /** Renderiza a capa e envia como mídia, devolvendo a URL para cover_url. */
  async function gerarCapa() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setBusy(true);
    setErro(null);
    try {
      render();
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92),
      );
      if (!blob) throw new Error('Não foi possível gerar a imagem da capa');

      const file = new File([blob], `capa-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const asset = await api.uploadMedia(file);
      onChange({ generated: asset });
    } catch (cause) {
      setErro(cause instanceof Error ? cause.message : 'Falha ao gerar a capa');
    } finally {
      setBusy(false);
    }
  }

  if (!isVideo) {
    return (
      <p className="muted" style={{ marginTop: 0 }}>
        A capa personalizada vale para vídeo (Reels). Envie o vídeo para escolher o frame.
      </p>
    );
  }

  const segundos = (cover.thumbOffsetMs / 1000).toFixed(1);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* O vídeo fica fora da tela: serve só de fonte de frames para o canvas. */}
      <video
        ref={videoRef}
        src={media!.publicUrl}
        muted
        playsInline
        preload="auto"
        /* crossOrigin é obrigatório: sem ele o canvas fica "tainted" e o
           toBlob para gerar a capa lança. Em troca, o container do Azure Blob
           PRECISA responder Access-Control-Allow-Origin — sem isso o vídeo nem
           carrega e a prévia fica preta, que foi o defeito relatado. */
        crossOrigin="anonymous"
        /* Fora da tela em vez de display:none: navegador tem liberdade de não
           decodificar frame de elemento sem caixa de layout, e aí o drawImage
           pinta preto mesmo com o vídeo carregado. */
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          left: -9999,
        }}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration || 0);
          // Posiciona já no offset salvo: sem isto a prévia abre no frame 0
          // mesmo com o slider indicando outro instante.
          e.currentTarget.currentTime = cover.thumbOffsetMs / 1000;
        }}
        onError={() =>
          setErro(
            'O navegador não conseguiu carregar o vídeo para gerar a prévia. ' +
              'Normalmente é CORS no armazenamento — avise que a capa não renderiza.',
          )
        }
      />

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <canvas
          ref={canvasRef}
          style={{
            width: 118,
            aspectRatio: '9 / 16',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: '#000',
            flexShrink: 0,
          }}
        />

        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 10 }}>
          <div>
            <label htmlFor="cover-off">
              Frame da capa — {segundos}s
              {duration > 0 && <span className="muted"> de {duration.toFixed(1)}s</span>}
            </label>
            <input
              id="cover-off"
              type="range"
              min={0}
              max={Math.max(Math.round((duration || 1) * 1000) - 100, 100)}
              step={100}
              value={cover.thumbOffsetMs}
              onChange={(e) => onChange({ thumbOffsetMs: Number(e.target.value), generated: null })}
            />
          </div>

          <div>
            <label htmlFor="cover-title">Título escrito na capa</label>
            <input
              id="cover-title"
              value={cover.title}
              onChange={(e) => onChange({ title: e.target.value, generated: null })}
              placeholder="ex: CABELO LISO EM 3 PASSOS"
              maxLength={60}
            />
          </div>

          {suggestedTitle && suggestedTitle !== cover.title && (
            <button
              type="button"
              className="ghost"
              style={{ justifySelf: 'start', fontSize: 12 }}
              onClick={() => onChange({ title: suggestedTitle, generated: null })}
            >
              ✨ Usar sugestão: “{suggestedTitle}”
            </button>
          )}

          {cover.title.trim() && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void gerarCapa()} disabled={busy}>
                {busy ? <span className="spinner" /> : cover.generated ? '↻ Regerar capa' : 'Gerar capa'}
              </button>
              {cover.generated ? (
                <span className="badge ok">capa pronta</span>
              ) : (
                <span className="muted" style={{ fontSize: 11.5 }}>
                  gere a capa para o título entrar na publicação
                </span>
              )}
            </div>
          )}

          {erro && <span className="muted" style={{ color: 'var(--err)' }}>{erro}</span>}

          <p className="muted" style={{ fontSize: 11.5, margin: 0 }}>
            {cover.title.trim()
              ? 'Com título, enviamos uma imagem de capa própria (cover_url).'
              : 'Sem título, enviamos só o instante do frame (thumb_offset) e o Instagram corta.'}
          </p>
        </div>
      </div>
    </div>
  );
}
